import { getPending, removePending } from "@/lib/local-db";
import { refreshData } from "@/lib/refresh";
import { toast } from "sonner";

const SYNC_EVENT = "outbox-sync";

// Module-level, not per-component: the pill and the global "connectivity is
// back" listener are two callers of one flush, and they must not overlap.
let syncing = false;

function setSyncing(next: boolean) {
  syncing = next;
  window.dispatchEvent(new Event(SYNC_EVENT));
}

export function isSyncing(): boolean {
  return syncing;
}

export function onSyncChange(cb: () => void): () => void {
  window.addEventListener(SYNC_EVENT, cb);
  return () => window.removeEventListener(SYNC_EVENT, cb);
}

// A 4xx means the server will reject this body every time, so retrying it
// forever just pins the outbox open. 401 is the exception — the session
// expired, and the same body succeeds after signing in again.
function isPermanent(status: number): boolean {
  return (
    status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429
  );
}

// Push every queued offline expense to the server. Successful ones are removed
// from the outbox; a network failure stays queued for the next attempt.
export async function flushOutbox(): Promise<{
  synced: number;
  failed: number;
  dropped: number;
}> {
  const pending = await getPending();
  let synced = 0;
  let failed = 0;
  let dropped = 0;

  for (const item of pending) {
    try {
      // The Go handler ignores unknown fields, so localId/queuedAt ride along
      // harmlessly (the old zod schema stripped them the same way).
      const res = await fetch("/api/expenses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item),
      });
      if (res.ok) {
        await removePending(item.localId);
        synced++;
      } else if (isPermanent(res.status)) {
        await removePending(item.localId);
        dropped++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }

  return { synced, failed, dropped };
}

// The one entry point for draining the outbox — guarded, and it refreshes the
// mounted views itself so callers do not each have to remember to.
export async function syncNow(): Promise<void> {
  if (syncing || !navigator.onLine) return;
  setSyncing(true);
  try {
    const { synced, dropped } = await flushOutbox();
    if (synced > 0) {
      toast.success(`Synced ${synced} expense${synced > 1 ? "s" : ""}`);
      refreshData();
    }
    if (dropped > 0) {
      toast.error(
        `${dropped} queued expense${dropped > 1 ? "s were" : " was"} rejected — re-enter ${dropped > 1 ? "them" : "it"}`,
      );
    }
  } finally {
    setSyncing(false);
  }
}

// Registered once at the root. The sync pill only lives on the overview, so
// without this a connection coming back on any other screen left the outbox
// sitting there until the user happened to navigate home.
export function startOutboxSync(): () => void {
  const onOnline = () => void syncNow();
  window.addEventListener("online", onOnline);
  void syncNow();
  return () => window.removeEventListener("online", onOnline);
}
